import { FC } from 'react';
import type { TooltipContentProps } from 'recharts';
import type { MaquinaSerie, VisaoGeralMes } from '@/types/dashboards';
import { faixaMfr, linhaDoTempo, type PontoLinhaDoTempo } from '@/utils/equiplexMaquinas';
import { fmtDiaMes, fmtPct, fmtUn } from '@/utils/painelFormat';
import { escalaDoDia } from '@/utils/rotulosGrafico';
import type { CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import PainelCard from '../PainelCard';
import TooltipBox from '../TooltipBox';
import ColunasDoDia from './ColunasDoDia';
import { visualFaixaMfr } from './visual';

interface Props {
  maquinas: MaquinaSerie[];
  mes: VisaoGeralMes;
  limiares: { alerta: number; critico: number };
  cores: CoresPainel;
}

const SITUACAO_TEXTO: Record<PontoLinhaDoTempo['situacao'], string> = {
  fechado: '',
  aberto: 'dia aberto: OPs ainda sendo reportadas',
  futuro: 'OP ainda não começou',
  sem_op: 'sem OP',
};

function conteudoTooltip(cores: CoresPainel) {
  const Conteudo = ({ active, payload }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as PontoLinhaDoTempo) : null;
    if (!p || p.situacao === 'sem_op') return null;
    const linhas = [
      { rotulo: 'Programado', valor: fmtUn(p.programado), cor: cores.programado },
      { rotulo: 'Produzido', valor: fmtUn(p.produzido), cor: cores.produzido },
      { rotulo: 'MFR', valor: fmtPct(p.mfr) },
      { rotulo: 'OPs', valor: fmtUn(p.ops) },
    ];
    const extra = SITUACAO_TEXTO[p.situacao];
    return <TooltipBox titulo={extra ? `${fmtDiaMes(p.data)} · ${extra}` : fmtDiaMes(p.data)} linhas={linhas} />;
  };
  return Conteudo;
}

const SeloMfr: FC<{ mfr: number | null; limiares: Props['limiares'] }> = ({ mfr, limiares }) => {
  const faixa = faixaMfr(mfr, limiares);
  if (!faixa) return <span className="text-xs text-content-muted">sem dia fechado</span>;
  const v = visualFaixaMfr(faixa);
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-content-primary" title={`MFR do mês: ${v.rotulo}`}>
      <span aria-hidden className="material-symbols-outlined text-[15px] leading-none" style={{ color: v.cor }}>
        {v.icone}
      </span>
      MFR {fmtPct(mfr)}
    </span>
  );
};

const GraficoMaquina: FC<{ serie: MaquinaSerie; mes: VisaoGeralMes; limiares: Props['limiares']; cores: CoresPainel }> = ({
  serie,
  mes,
  limiares,
  cores,
}) => {
  const pontos = linhaDoTempo(serie, mes);
  const escala = escalaDoDia(pontos.map((p) => p.produzido));
  return (
    <div className="min-w-0 rounded-lg border border-theme-border p-3" data-testid={`maq-tempo-${serie.maquina}`}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-content-primary">{serie.maquina}</h3>
        <span className="flex items-center gap-3">
          <span className="text-xs text-content-muted">produzido em {escala.unidade}</span>
          <SeloMfr mfr={serie.mes_total.mfr} limiares={limiares} />
        </span>
      </div>
      <ColunasDoDia
        pontos={pontos.map((p) => ({ ...p, esmaecido: p.situacao !== 'fechado' }))}
        cores={cores}
        tooltip={conteudoTooltip(cores)}
        formatar={escala.formatar}
      />
    </div>
  );
};

/**
 * Um gráfico por máquina: programado × produzido dia a dia no mês (o que o "Por máquina" do
 * Manus tentava mostrar, com a máquina e o número de verdade). Dia aberto e futuro ficam claros.
 * Uma máquina por linha, na largura toda, com o produzido de cada dia escrito (26/10/2026).
 */
const LinhaDoTempoMaquinas: FC<Props> = ({ maquinas, mes, limiares, cores }) => (
  <PainelCard
    testId="maq-linha-do-tempo"
    titulo="Linha do tempo por máquina"
    subtitulo={`${mes.label}: cada coluna é o dia em que as OPs começaram. Coluna clara = dia aberto ou OP ainda por começar.`}
  >
    <Legenda
      itens={[
        { rotulo: 'Programado (OPs que começam no dia): contorno tracejado', cor: cores.programado, tracejado: true },
        { rotulo: 'Produzido (reportado nas OPs)', cor: cores.produzido },
      ]}
    />
    {maquinas.length === 0 ? (
      <div className="py-10 text-center text-sm text-content-muted">Nenhuma OP neste mês.</div>
    ) : (
      <div className="flex flex-col gap-3">
        {maquinas.map((s) => (
          <GraficoMaquina key={s.maquina} serie={s} mes={mes} limiares={limiares} cores={cores} />
        ))}
      </div>
    )}
  </PainelCard>
);

export default LinhaDoTempoMaquinas;
